/**
 * Tracks the pending human-review count for a page's hidden comments.
 * Pulls once on mount/page change, then keeps the count in sync via the
 * page socket room: increments on `new-comment` (hidden), decrements on
 * `comment-reviewed`, and refetches on `comment-updated` to catch state
 * transitions not directly observable from event payloads.
 */
import { useEffect, useRef, useState } from "react";
import { useApi, API_ENDPOINTS } from "../lib/api";
import {
  getSocket,
  joinPageRoom,
  leavePageRoom,
} from "../lib/socket";

export function useReviewCount(pageId: string | null | undefined) {
  const api = useApi();
  const [count, setCount] = useState<number>(0);
  const pageIdRef = useRef<string | null | undefined>(pageId);

  useEffect(() => {
    pageIdRef.current = pageId;
  }, [pageId]);

  useEffect(() => {
    let cancelled = false;
    if (!pageId) {
      setCount(0);
      return;
    }

    const fetchCount = async () => {
      try {
        const res = await api.get(API_ENDPOINTS.REVIEW_QUEUE_COUNT(pageId));
        if (!cancelled) setCount(res.data?.count ?? 0);
      } catch (err) {
        // Non-fatal: badge just won't update.
        console.error("[useReviewCount] fetch failed", err);
      }
    };

    fetchCount();
    joinPageRoom(pageId);
    const socket = getSocket();

    const handleNewComment = (c: any) => {
      if (pageIdRef.current !== pageId) return;
      const status = c?.status || c?.moderationStatus;
      if (status === "hidden") setCount((n) => n + 1);
    };
    const handleCommentReviewed = (r: any) => {
      if (pageIdRef.current !== pageId) return;
      if (r?.commentId) setCount((n) => Math.max(0, n - 1));
    };
    const handleCommentUpdated = (u: any) => {
      // Status transitions in/out of hidden are not always reflected in the
      // payload — safest to refetch on these to avoid drift.
      if (pageIdRef.current !== pageId) return;
      if (!u?.id) return;
      fetchCount();
    };

    socket.on("new-comment", handleNewComment);
    socket.on("comment-reviewed", handleCommentReviewed);
    socket.on("comment-updated", handleCommentUpdated);

    return () => {
      cancelled = true;
      socket.off("new-comment", handleNewComment);
      socket.off("comment-reviewed", handleCommentReviewed);
      socket.off("comment-updated", handleCommentUpdated);
      leavePageRoom(pageId);
    };
  }, [pageId]);

  return count;
}
